import React, { useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Dropzonify from "../../../support/components/dropzonify";
import withAjaxStatus from "../../../support/hoc/withAjaxStatus";

import { validateStudentSpecialGroupImport, importStudentSpecialGroups } from "./apis";
import "../schools/Edit.css";
import "./BulkImport.css";

const STEP_UPLOAD = "upload";
const STEP_REVIEW = "review";
const STEP_DONE = "done";

let BulkImport = ({ accessToken, ajaxStatusLoading, ajaxStatusComplete, ajaxStatusError }) => {
    const [step, setStep] = useState(STEP_UPLOAD);
    const [documentId, setDocumentId] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(null);

    const onUploadComplete = async (document) => {
        try {
            setBusy("Validating file...");
            ajaxStatusLoading();
            const validation = (await validateStudentSpecialGroupImport(document.id)).data.result;
            setDocumentId(document.id);
            setResult(validation);
            setStep(STEP_REVIEW);
            ajaxStatusComplete();
        } catch (err) {
            ajaxStatusError(err);
        } finally {
            setBusy(null);
        }
    };

    const onConfirm = async () => {
        try {
            setBusy("Importing assignments...");
            ajaxStatusLoading();
            const importResult = (await importStudentSpecialGroups(documentId)).data.result;
            setResult(importResult);
            setStep(STEP_DONE);
            ajaxStatusComplete();
            toast.success("Import complete.");
        } catch (err) {
            ajaxStatusError(err);
        } finally {
            setBusy(null);
        }
    };

    const onReset = () => {
        setStep(STEP_UPLOAD);
        setDocumentId(null);
        setResult(null);
    };

    const renderErrorTable = () => {
        if (!result || result.errorRows.length === 0) {
            return <p>No errors found.</p>;
        }
        return (
            <table className="bulk-import__table">
                <thead>
                    <tr>
                        <th>Row</th>
                        <th>CrusaderId</th>
                        <th>Special Group</th>
                        <th>Error</th>
                    </tr>
                </thead>
                <tbody>
                    {result.errorRows.map((row) => (
                        <tr key={row.rowNumber}>
                            <td>{row.rowNumber}</td>
                            <td>{row.crusaderId}</td>
                            <td>{row.specialGroupName}</td>
                            <td>{row.error}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className="bulk-import">
            <div className="bulk-import__header">
                <h2>Bulk Assign Special Groups</h2>
                <Link className="bulk-import__back" to="/admin/specialgroups">
                    Back to Special Groups
                </Link>
            </div>

            {busy && (
                <div className="bulk-import__busy">
                    <span className="bulk-import__spinner" />
                    <span>{busy}</span>
                </div>
            )}

            {step === STEP_UPLOAD && !busy && (
                <>
                    <p>
                        Upload an Excel (.xlsx) file with two columns headed <strong>CrusaderId</strong> and{" "}
                        <strong>SpecialGroupName</strong>. CrusaderId must match a student&apos;s Onward Id, and the group
                        name must match an active special group. The file will be validated before anything is saved.
                    </p>
                    <div className="bulk-import__dropzone">
                        <Dropzonify
                            accessToken={accessToken}
                            accept=".xlsx"
                            uploadUrl="/api/services/app/documents/upload"
                            onUploadComplete={onUploadComplete}
                            additionalUploadParameters={{ groupType: 0 }}
                        >
                            {({ uploadingFiles, onBrowse, dragging }) => (
                                <div className={`bulk-import__drop ${dragging ? "bulk-import__drop--hover" : ""}`}>
                                    {uploadingFiles.length > 0 ? (
                                        <div className="bulk-import__uploading">
                                            <span className="bulk-import__spinner" />
                                            <span>
                                                Uploading {uploadingFiles[0].fileName}... {uploadingFiles[0].percentComplete}%
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            <span>Drag and drop your .xlsx file here, or</span>
                                            <button type="button" className="bulk-import__browse" onClick={onBrowse}>
                                                Browse for file
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </Dropzonify>
                    </div>
                </>
            )}

            {step === STEP_REVIEW && result && (
                <>
                    <h3>Review</h3>
                    <ul className="bulk-import__summary">
                        <li>Total rows: {result.totalRows}</li>
                        <li className="bulk-import__valid">Valid (will be assigned): {result.validCount}</li>
                        <li className="bulk-import__error">Errors (will be skipped): {result.errorCount}</li>
                    </ul>
                    {renderErrorTable()}
                    <div className="bulk-import__actions">
                        <button
                            type="button"
                            className="bulk-import__confirm"
                            onClick={onConfirm}
                            disabled={result.validCount === 0}
                        >
                            Confirm Import ({result.validCount})
                        </button>
                        <button type="button" className="bulk-import__cancel" onClick={onReset}>
                            Cancel
                        </button>
                    </div>
                </>
            )}

            {step === STEP_DONE && result && (
                <>
                    <h3>Import Complete</h3>
                    <ul className="bulk-import__summary">
                        <li className="bulk-import__valid">Assigned: {result.validCount}</li>
                        <li className="bulk-import__error">Skipped (errors): {result.errorCount}</li>
                    </ul>
                    {renderErrorTable()}
                    <div className="bulk-import__actions">
                        <button type="button" className="bulk-import__confirm" onClick={onReset}>
                            Import Another File
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    accessToken: state.user.accessToken,
});

BulkImport = withAjaxStatus(BulkImport);
BulkImport = connect(mapStateToProps)(BulkImport);

export default BulkImport;
